import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { GoogleService } from '../services/GoogleService';
import { Capacitor } from '@capacitor/core';
import { setGoogleUserData } from '../utils/googleUserData';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isInitialMount = React.useRef(true);
  const isLoggedInBefore = React.useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
      if (initialSession) {
        isLoggedInBefore.current = true;
      }
      isInitialMount.current = false;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('onAuthStateChange event:', event, 'session:', currentSession);
        const isUserLoggedInNow = currentSession?.user !== null;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (event === 'SIGNED_IN') {
          console.log('onAuthStateChange: SIGNED_IN. User is logged in now:', isUserLoggedInNow, 'Was logged in before:', isLoggedInBefore.current, 'Is initial mount:', isInitialMount.current);

          if (!isLoggedInBefore.current) {
            toast.success('¡Sesión iniciada con éxito!');
          }

          if (window.location.pathname === '/login') {
            console.log('Redirigiendo a perfil desde login después de SIGNED_IN.');
            setTimeout(() => {
              navigate('/profile');
            }, 0);
          }

        } else if (event === 'SIGNED_OUT') {
          console.log('onAuthStateChange: SIGNED_OUT.');
          toast.info('Sesión cerrada');
          setTimeout(() => {
            navigate('/');
          }, 0);
        } else if (event === 'USER_UPDATED') {
          console.log('onAuthStateChange: USER_UPDATED.');
        }

        isLoggedInBefore.current = isUserLoggedInNow;
      }
    );

    App.addListener('resume', async () => {
      console.log('Capacitor App resumed event.');
      supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
        console.log('getSession on resume:', currentSession);
      });
    });

    App.addListener('appUrlOpen', async (event) => {
      console.log('Evento appUrlOpen recibido:', event);
      const url = new URL(event.url);
      console.log('Deep link recibido:', event.url);

      console.log('Protocolo:', url.protocol, 'Hostname:', url.hostname, 'Pathname:', url.pathname, 'Search:', url.search);

      if (
        (url.hostname === 'sangre-fuerte.millev.com' && url.pathname === '/' && url.search.includes('code=')) ||
        (url.hostname === 'sangre-fuerte.millev.com' && url.pathname === '/auth/callback') ||
        (url.protocol === 'com.sangrefuerte.app:' && url.hostname === 'auth' && url.pathname === '/callback')
      ) {
        console.log('URL de callback detectada desde deep link, procesando...');

        try {
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) {
            console.error('Error al obtener la sesión desde deep link:', error);
            toast.error('Error al reestablecer la sesión');
            navigate('/login');
          } else if (session) {
            console.log('Sesión obtenida desde deep link. onAuthStateChange debería manejar el resto.');
          } else {
            console.error('No se encontró sesión después del callback deep link.');
            toast.error('No se pudo reestablecer la sesión');
            navigate('/login');
          }
        } catch (error) {
          console.error('Error inesperado en el callback deep link:', error);
          if (error instanceof Error) {
            toast.error(`Error inesperado: ${error.message}`);
          } else {
            toast.error('Error inesperado al procesar el deep link.');
          }
          navigate('/login');
        }
      } else {
        console.log('Deep link recibido pero no es una URL de callback de autenticación conocida.', url.toString());
      }
    });

    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
      App.removeAllListeners();
    };

  }, [navigate]);

  const signInWithGoogle = async () => {
    const isMobileApp = Capacitor.isNativePlatform();
    console.log('¿Es aplicación móvil?', isMobileApp);
    console.log('Plataforma:', Capacitor.getPlatform());

    try {
      if (isMobileApp) {
        console.log('Intentando autenticación con GoogleService (móvil)...');
        const googleService = new GoogleService();
        const loginResult = await googleService.login();

        if (loginResult && loginResult.idToken) {
          console.log('ID Token obtenido de GoogleService. Autenticando en Supabase con ID token...');
          console.log('Datos del usuario completos:', JSON.stringify(loginResult.user, null, 2));
          console.log('URL de imagen específica:', loginResult.user?.imageUrl);
          console.log('Nombre específico:', loginResult.user?.name);
          
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: loginResult.idToken,
          });

          if (error) {
            console.error('Error al autenticar en Supabase con ID token:', error);
            toast.error(`Error de Supabase: ${error.message}`);
          } else if (data.session) {
            console.log('Sesión de Supabase establecida con ID token. onAuthStateChange se disparará.');
            
            // Almacenar datos del usuario de Google en localStorage como respaldo
            if (loginResult.user) {
              console.log('Almacenando datos del usuario de Google en localStorage...');
              const googleUserData = {
                full_name: loginResult.user.name,
                avatar_url: loginResult.user.imageUrl,
                given_name: loginResult.user.givenName,
                family_name: loginResult.user.familyName,
                email: loginResult.user.email
              };
              
              setGoogleUserData(googleUserData);
              
              // Intentar actualizar metadatos en Supabase (opcional)
              try {
                const { data: updateData, error: updateError } = await supabase.auth.updateUser({
                  data: googleUserData
                });
                
                if (updateError) {
                  console.warn('No se pudieron actualizar metadatos en Supabase (usando localStorage):', updateError);
                } else {
                  console.log('Metadatos actualizados exitosamente en Supabase');
                }
              } catch (error) {
                console.warn('Error al actualizar metadatos en Supabase (usando localStorage):', error);
              }
            }
          } else {
            console.error('Supabase no retornó sesión después de signInWithIdToken');
            toast.error('No se pudo establecer la sesión en Supabase.');
          }

        } else {
          console.error('GoogleService.login() no retornó ID token.');
          toast.error('Error al obtener token de Google.');
        }

      } else {
        console.log('Intentando autenticación con Supabase signInWithOAuth (Web)...');
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        });

        if (error) {
          console.error('Error de autenticación en Supabase OAuth (Web):', error);
          toast.error(`Error al iniciar sesión: ${error.message}`);
        } else if (data?.url) {
          console.log('URL de redirección OAuth generada (Web):', data.url);
          window.location.href = data.url;
        }
      }
    } catch (error) {
      console.error('Error general en signInWithGoogle:', error);
      if (error instanceof Error) {
        toast.error(`Error en autenticación: ${error.message}`);
      } else {
        toast.error('Error de autenticación desconocido.');
      }
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      console.log('Sesión cerrada correctamente en Supabase.');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      if (error instanceof Error) {
        toast.error(`Error al cerrar sesión: ${error.message}`);
      } else {
        toast.error('Error desconocido al cerrar sesión.');
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 